export type ChipState = 'green' | 'amber' | 'red';

export interface ChipConfig {
  state: ChipState;
  label: string;
  classes: string;
}

/**
 * Shared green/amber/red compliance-chip config, extracted from what were
 * two copy-pasted `getChipConfig` functions (`ComplianceCard.tsx` and
 * `FireDrillLogPage.tsx`) once the incident register became a third
 * consumer of the exact same cadence-status vocabulary (Task 22).
 *
 * Compared line-by-line before extracting: the two originals differed only
 * in dead code — `ComplianceCard`'s had a redundant
 * `daysInMonthRemaining < 7` branch whose `if` and (implicit) `else` both
 * returned the identical amber object, which `FireDrillLogPage`'s version
 * had already collapsed away. Output is identical for every input in both;
 * this file keeps the simpler (collapsed) form.
 *
 * NOT the same thing as `IncidentSeverityChip`/`getSeverityChipConfig`
 * (`components/incidents/IncidentSeverityChip.tsx`) — severity is a fixed
 * enum (MINOR/SIGNIFICANT/NOTIFIABLE), this is a time-based cadence
 * calculation (days remaining until a compliance window closes). Different
 * shape, deliberately not merged.
 */
export function getChipConfig(isCompliant: boolean, daysInMonthRemaining: number): ChipConfig {
  if (isCompliant) {
    return {
      state: 'green',
      label: 'Drilled this month',
      classes: 'bg-emerald-100 text-emerald-800',
    };
  }
  if (daysInMonthRemaining <= 0) {
    return {
      state: 'red',
      label: 'OVERDUE',
      classes: 'bg-rose-100 text-rose-800',
    };
  }
  return {
    state: 'amber',
    label: `${daysInMonthRemaining} days left`,
    classes: 'bg-amber-100 text-amber-800',
  };
}
